import { PostReactionType } from '__generated__/graphql/legacy/graphql';
import classNames from 'classnames/bind';
import { features } from 'constants/featureFlags';
import { getReactors } from 'helpers/reactionHelpers';
import { useTranslation } from 'react-i18next';
import { Post, Reply } from 'types/post';
import { REACTION_TYPES } from 'utils/constants';
import { canPostInReadOnlySpace } from 'utils/userHelpers';

import React from 'react';

import { Button } from '@thinkific/chalk/Button';
import { Tooltip } from '@thinkific/chalk/Tooltip';
import { ChatCircle, ChatCircleText } from '@thinkific/chalk/react-icons';

import { useCommunityContext } from 'components/CommunityContext';
import ReactionButton from 'components/ReactionButton';
import ReactionBar from 'components/new/Posts/ReactionBar';

import styles from '../Post.module.scss';

const cx = classNames.bind(styles);

interface PostFooterProps {
  hasReacted: boolean;
  post: Post | Reply;
  openReplyPanel: () => void;
  totalCommentCount: number;
  postReactionCount: number;
  isReplyPanel: boolean;
  onReactionClick: (reactionType: PostReactionType) => void;
  className?: string;
}

const PostFooter = ({
  hasReacted,
  post,
  postReactionCount,
  openReplyPanel,
  totalCommentCount,
  isReplyPanel,
  onReactionClick,
  className,
}: PostFooterProps) => {
  const { t } = useTranslation();
  const { currentUser } = useCommunityContext();

  // Note: Only Posts have communitySpace, Replies inherit from their parent Post
  const isReadOnlySpace = (post as Post)?.communitySpace?.readOnly || false;

  // disable reply button if:
  // 1. In a read-only space, AND
  // 2. User cannot post in read-only spaces, AND
  // 3. There are no existing comments
  const replyButtonDisabled =
    isReadOnlySpace && !canPostInReadOnlySpace(currentUser) && totalCommentCount === 0;
  const { featureFlags } = useCommunityContext();
  const multipleReactionsEnabled = featureFlags[features.COMMUNITY_REACTIONS];

  const replyButton = ({ disabled = false }: { disabled?: boolean }) => {
    return (
      <Button
        data-qa="main-comments_text"
        disabled={disabled}
        emphasis="low"
        icon={totalCommentCount > 0 ? ChatCircleText : ChatCircle}
        iconPosition="lead"
        size="sm"
        onClick={openReplyPanel}
      >
        {totalCommentCount > 0 ? t('{{count}} reply', { count: totalCommentCount }) : t('Reply')}
      </Button>
    );
  };

  return (
    <div
      className={cx(
        'post__footer',
        { 'multiple-reactions-enabled': multipleReactionsEnabled },
        className
      )}
    >
      <>
        {multipleReactionsEnabled ? (
          <ReactionBar onReactionClick={onReactionClick} />
        ) : (
          <ReactionButton
            hasReacted={hasReacted}
            id={post.id}
            isReactingTo="post"
            postReactionCount={postReactionCount}
            reactionType={REACTION_TYPES.LIKE_POST}
            reactors={getReactors(post)}
            t={t}
          />
        )}
        {!isReplyPanel &&
          (replyButtonDisabled ? (
            <Tooltip label={t('components-postFooter-read_only_tooltip', 'Read only')}>
              {replyButton({ disabled: true })}
            </Tooltip>
          ) : (
            replyButton({ disabled: false })
          ))}
      </>
    </div>
  );
};

export default PostFooter;
